<script lang="ts" setup>
definePageMeta({
  layout: 'menu',
})

const {
  public: { siteUrl, webAppLink },
} = useRuntimeConfig()

const breakpoints = useBreakpoints({
  laptop: 1024,
  desktop: 1280,
})

const { t, locale } = useI18n()
const localeRoute = useLocaleRoute()

function resolveHomeLink(path: string) {
  if (path === '/checkin') {
    return path
  }

  return localeRoute(path)
}

useSeoMeta({
  title: t('home-ai-hero-title'),
  description: t('home-ai-hero-subtitle'),
  ogTitle: t('home-ai-hero-title'),
  ogDescription: t('home-ai-hero-subtitle'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1000',
  ogImageHeight: '300',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('home-ai-hero-title'),
  twitterDescription: t('home-ai-hero-subtitle'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

const smallerThanXl = breakpoints.smaller('desktop')
const largerThanXl = breakpoints.greater('desktop')

const isRuLocale = computed(() => locale.value === 'ru')
const isNotRuLocale = computed(() => locale.value !== 'ru')

const partners = [
  {
    id: 1,
    img: '/images/home/icons/google-cloud.png',
  },
  {
    id: 2,
    img: '/images/home/icons/open-ai.png',
  },
  {
    id: 3,
    img: '/images/home/icons/digital-ocean.png',
  },
  {
    id: 4,
    img: '/images/home/icons/segment.png',
  },
  {
    id: 5,
    img: '/images/home/icons/tencent-cloud.png',
  },
  {
    id: 6,
    img: '/images/home/icons/aws.png',
  },
  {
    id: 7,
    img: '/images/home/icons/sui-logo.png',
  },
  {
    id: 8,
    img: '/images/home/icons/starknet-logo.png',
  },
]

const features = ref([
  {
    id: 1,
    title: t('home-ai-feature-venue-rewards'),
    img: breakpoints.desktop.value
      ? '/images/home/tokenized_location_laptop.png'
      : '/images/home/tokenized_location.png',
    link: '/checkin',
  },
  {
    id: 2,
    title: t('home-ai-feature-agent-tools'),
    img: '/images/home/ai_raffles.png',
    link: '/ai',
  },
  {
    id: 3,
    title: t('home-ai-feature-ticketing'),
    img: '/images/home/nft_tickets.png',
    link: '/nft-tickets',
  },
  {
    id: 4,
    title: t('home-ai-feature-guest-ops'),
    img: '/images/home/ai_raffles.png',
    link: '/ai',
  },
  {
    id: 5,
    title: t('home-ai-feature-checkin-rewards'),
    img: '/images/home/onchain_bounties.png',
    link: '/checkin',
  },
])

const openClawFeatures = [
  {
    id: 1,
    title: t('home-ai-workflow-1-title'),
    text: t('home-ai-workflow-1-text'),
    icon: 'lucide:message-circle-question',
    accent:
      'linear-gradient(85.11deg, #7540ee -2.65%, #4467ff 47.09%, #ff8fe9 93.65%)',
  },
  {
    id: 2,
    title: t('home-ai-workflow-2-title'),
    text: t('home-ai-workflow-2-text'),
    icon: 'lucide:messages-square',
    accent:
      'linear-gradient(85.11deg, #31d0aa -2.65%, #4467ff 47.09%, #8c5cff 93.65%)',
  },
  {
    id: 3,
    title: t('home-ai-workflow-3-title'),
    text: t('home-ai-workflow-3-text'),
    icon: 'lucide:hand-coins',
    accent:
      'linear-gradient(85.11deg, #ff8fe9 -2.65%, #7540ee 47.09%, #54d5ff 93.65%)',
  },
  {
    id: 4,
    title: t('home-ai-workflow-4-title'),
    text: t('home-ai-workflow-4-text'),
    icon: 'lucide:megaphone',
    accent:
      'linear-gradient(85.11deg, #54d5ff -2.65%, #4467ff 47.09%, #ff8fe9 93.65%)',
  },
  {
    id: 5,
    title: t('home-ai-workflow-5-title'),
    text: t('home-ai-workflow-5-text'),
    icon: 'lucide:workflow',
    accent:
      'linear-gradient(85.11deg, #7540ee -2.65%, #31d0aa 47.09%, #4467ff 93.65%)',
  },
  {
    id: 6,
    title: t('home-ai-workflow-6-title'),
    text: t('home-ai-workflow-6-text'),
    icon: 'lucide:blocks',
    accent:
      'linear-gradient(85.11deg, #4467ff -2.65%, #ff8fe9 47.09%, #7540ee 93.65%)',
  },
]

const useCases = [
  {
    id: 1,
    title: t('home-ai-use-case-1'),
    image: '/images/home/icons/bounty-marketplace.png',
  },
  {
    id: 2,
    title: t('home-ai-use-case-2'),
    image: '/images/home/icons/nft-sbt-tickets.png',
  },
  {
    id: 3,
    title: t('home-ai-use-case-3'),
    image: '/images/home/icons/ai-raffles.png',
  },
  {
    id: 4,
    title: t('home-ai-use-case-4'),
    image: '/images/home/icons/smart-access-depin.png',
  },
  {
    id: 5,
    title: t('home-ai-use-case-5'),
    image: '/images/home/icons/content-gating.png',
  },
  {
    id: 6,
    title: t('home-ai-use-case-6'),
    image: '/images/home/icons/physical-verification.png',
  },
]

const product = [
  {
    id: 1,
    title: t('home-ai-product-1'),
    image: '/images/home/icons/apple-google.png',
  },
  {
    id: 2,
    title: t('home-ai-product-2'),
    image: '/images/home/icons/zero-friction.png',
  },
  {
    id: 3,
    title: t('home-ai-product-3'),
    image: '/images/home/icons/gasless-transactions.png',
  },
  {
    id: 4,
    title: t('home-ai-product-4'),
    image: '/images/home/icons/multichain.png',
  },
  {
    id: 5,
    title: t('home-ai-product-5'),
    image: '/images/home/icons/app-apple-google.png',
  },
]
const product1 = [
  {
    id: 1,
    title: t('home-ai-product-6'),
    image: '/images/home/icons/ai-engagement.png',
  },
  {
    id: 2,
    title: t('home-ai-product-7'),
    image: '/images/home/icons/api-sdk.png',
  },
  {
    id: 3,
    title: t('home-ai-product-8'),
    image: '/images/home/icons/location-tokens.png',
  },
  {
    id: 4,
    title: t('home-ai-product-9'),
    image: '/images/home/icons/whitelabel.png',
  },
]

const belongTransforms = ref([
  {
    id: 1,
    title: t('home-ai-transform-venues-title'),
    description: t('home-ai-transform-venues-text'),
    image: '/images/home/brands.png',
  },
  {
    id: 2,
    title: t('home-ai-transform-organizers-title'),
    description: t('home-ai-transform-organizers-text'),
    image: '/images/home/communities.png',
  },
  {
    id: 3,
    title: t('home-ai-transform-promoters-title'),
    description: t('home-ai-transform-promoters-text'),
    image: '/images/home/users.png',
  },
  {
    id: 4,
    title: t('home-ai-transform-guests-title'),
    description: t('home-ai-transform-guests-text'),
    image: '/images/home/developers.png',
  },
])

const operatingLoop = [
  {
    id: 1,
    title: t('home-ai-loop-1-title'),
    text: t('home-ai-loop-1-text'),
    icon: 'lucide:bot',
  },
  {
    id: 2,
    title: t('home-ai-loop-2-title'),
    text: t('home-ai-loop-2-text'),
    icon: 'lucide:scan-line',
  },
  {
    id: 3,
    title: t('home-ai-loop-3-title'),
    text: t('home-ai-loop-3-text'),
    icon: 'lucide:ticket-check',
  },
]

const nowShipping = [
  {
    id: 1,
    title: t('home-ai-now-1-title'),
    text: t('home-ai-now-1-text'),
    icon: 'lucide:messages-square',
  },
  {
    id: 2,
    title: t('home-ai-now-2-title'),
    text: t('home-ai-now-2-text'),
    icon: 'lucide:map-pin-check',
  },
  {
    id: 3,
    title: t('home-ai-now-3-title'),
    text: t('home-ai-now-3-text'),
    icon: 'lucide:badge-check',
  },
  {
    id: 4,
    title: t('home-ai-now-4-title'),
    text: t('home-ai-now-4-text'),
    icon: 'lucide:chart-no-axes-combined',
  },
]
</script>

<template>
  <div>
    <div class="flex flex-col gap-3.5 text-center relative">
      <div class="h-166 relative overflow-hidden z-2 lg:h-175">
        <NuxtImg
          fetchpriority="high"
          format="webp"
          src="/images/home/gradient.png"
          alt=""
          width="1343"
          height="1045"
          class="object-cover h-full absolute z-1"
        />
        <SplineHero />
        <div
          class="h-25.5 w-full bg-gradient-to-t from-[#151515] to-transparent absolute bottom-0 z-4"
        />
      </div>

      <div
        class="absolute px-5 flex flex-col gap-3.5 z-5 left-0 right-0 max-lg:bottom-0 lg:top-87.5 lg:left-34.5 lg:right lg:text-left"
      >
        <h1
          class="font-extrabold leading-12 uppercase"
          :class="{
            'text-[2.5rem]': isRuLocale,
            'text-[3rem] lg:text-[3.375rem]': isNotRuLocale,
          }"
        >
          {{ t('home-ai-hero-title') }}
        </h1>

        <h2 class="text-xl tracking-wide uppercase leading-6 lg:max-w-175">
          {{ t('home-ai-hero-subtitle') }}
        </h2>

        <div
          class="flex flex-wrap gap-3 justify-center lg:justify-start lg:mt-8"
        >
          <ReusableInstallButtons />

          <NuxtLink
            to="https://aideploy.co"
            target="_blank"
            rel="noopener noreferrer"
            class="p-0.25 rounded-full overflow-hidden"
            style="
              background: linear-gradient(
                85.11deg,
                #7540ee -2.65%,
                #4467ff 47.09%,
                #ff8fe9 93.65%
              );
            "
          >
            <div
              class="bg-[#000000] rounded-full flex flex-row items-center justify-center gap-2 py-3 px-6"
            >
              <Icon name="lucide:bot" class="w-5 h-5" />
              <p class="uppercase font-medium text-sm tracking-wide">
                {{ t('home-ai-hero-primary-cta') }}
              </p>
            </div>
          </NuxtLink>

          <NuxtLink
            to="/checkin"
            class="border border-[#7540EE] rounded-full flex flex-row items-center justify-center gap-2 py-3 px-6 bg-black/40 backdrop-blur-md"
          >
            <Icon name="lucide:scan-line" class="w-5 h-5 text-[#CB99FF]" />
            <p class="uppercase font-medium text-sm tracking-wide">
              {{ t('home-ai-hero-secondary-cta') }}
            </p>
          </NuxtLink>
        </div>
      </div>
    </div>

    <div class="max-w-268 mx-auto px-5 mt-15 lg:mt-0">
      <div
        class="grid grid-cols-1 gap-4 md:grid-cols-3"
      >
        <div
          v-for="item in operatingLoop"
          :key="item.id"
          class="rounded-xl border border-[#2B2B2B] bg-[#0D0D0D]/70 p-5 backdrop-blur-lg"
        >
          <div
            class="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#7540EE33] text-[#CB99FF]"
          >
            <Icon :name="item.icon" class="size-6" />
          </div>
          <p class="font-semibold text-lg tracking-wide uppercase mb-2">
            {{ item.title }}
          </p>
          <p class="text-[#969696] text-sm lg:text-base leading-5 tracking-wide">
            {{ item.text }}
          </p>
        </div>
      </div>
    </div>

    <!-- AI Deploy Section -->
    <div class="max-w-268 mx-auto px-5 pt-20 lg:pt-28">
      <div class="text-center mb-12 lg:mb-16">
        <p
          class="text-[#7540EE] text-base tracking-wider uppercase pb-4"
        >
          {{ t('home-ai-workflows-label') }}
        </p>
        <h2
          class="font-bold text-[2rem] lg:text-[2.75rem] uppercase tracking-wide leading-tight"
          style="
            background: linear-gradient(
                107.92deg,
                #4d61fd 37.78%,
                #bb2bbf 106.06%
              )
              text;
            -webkit-text-fill-color: transparent;
          "
        >
          {{ t('home-ai-workflows-title') }}
        </h2>
        <p
          class="text-[#969696] text-base lg:text-lg tracking-wide leading-6 max-w-200 mx-auto mt-4"
        >
          {{ t('home-ai-workflows-subtitle') }}
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="feature in openClawFeatures"
          :key="feature.id"
          class="openclaw-card common-gradient"
          :style="{ '--openclaw-accent': feature.accent }"
        >
          <div class="openclaw-card__accent" />
          <div class="openclaw-card__icon" aria-hidden="true">
            <Icon :name="feature.icon" class="size-6" />
          </div>
          <p class="font-semibold text-lg tracking-wide uppercase mb-3">
            {{ feature.title }}
          </p>
          <p class="text-[#969696] text-sm lg:text-base leading-5 tracking-wide">
            {{ feature.text }}
          </p>
        </div>
      </div>

      <div class="flex justify-center mt-10">
        <NuxtLink
          to="https://aideploy.co"
          target="_blank"
          rel="noopener noreferrer"
          class="p-0.25 rounded-full overflow-hidden"
          style="
            background: linear-gradient(
              85.11deg,
              #7540ee -2.65%,
              #4467ff 47.09%,
              #ff8fe9 93.65%
            );
          "
        >
          <div
            class="bg-[#000000] rounded-full flex flex-row items-center gap-2 py-3 px-6"
          >
            <p class="uppercase font-medium text-sm tracking-wide">
              {{ t('home-ai-workflows-cta') }}
            </p>
          </div>
        </NuxtLink>
      </div>
    </div>
    <!-- End AI Deploy Section -->

    <div class="max-w-360 mx-auto relative">
      <p
        class="text-[#7540EE] text-base tracking-wider text-center pb-7.5 uppercase pt-11"
      >
        {{ t('home-ai-integrations-label') }}
      </p>

      <div
        class="w-30 h-full absolute left-0 top-0 m-auto bg-gradient-to-r z-9 from-[#151515] to-transparent"
      />
      <UCarousel
        v-slot="{ item }"
        loop
        :auto-scroll="{
          speed: 1,
          stopOnInteraction: false,
          stopOnMouseEnter: false,
        }"
        :items="partners"
        :ui="{
          item: 'basis-auto flex justify-center ps-0 px-20',
        }"
      >
        <NuxtImg :src="item.img" class="object-contain opacity-40 h-7.5" />
      </UCarousel>

      <div
        class="w-30 h-full absolute right-0 top-0 m-auto bg-gradient-to-r z-9 to-[#151515] from-transparent"
      />
    </div>
    <div class="pt-10 lg:pt-19">
      <ClientOnly>
        <div
          v-if="largerThanXl"
          class="flex flex-row flex-wrap gap-3 max-w-268 mx-auto"
        >
          <NuxtLink
            :to="resolveHomeLink(item.link)"
            v-for="item in features"
            :key="item.id"
            class="flex"
            :class="{
              'w-145 h-81': item.id === 1,
              'w-120 ': item.id === 2,
              'w-91 h-75': item.id === 3,
              'w-85.5 ': item.id === 4 || item.id === 5,
            }"
          >
            <div
              class="rounded-[1.25rem] relative w-full feature-gradient common-gradient"
            >
              <p
                class="font-medium text-xl tracking-wide uppercase left-6 top-6 right-10.5 absolute text-left leading-6"
                :class="{ 'max-w-75': item.id === 1 }"
              >
                {{ item.title }}
              </p>
              <NuxtImg
                loading="lazy"
                format="webp"
                class="w-full h-full object-cover rounded-[1.25rem]"
                alt=""
                :src="item.img"
              />
            </div>
          </NuxtLink>
        </div>
        <UCarousel
          v-if="smallerThanXl"
          v-slot="{ item }"
          dots
          :items="features"
          :ui="{
            container: 'm-auto ps-0 ',
            item: 'basis-auto max-w-75 min-[400px]:max-w-95 sm:max-w-100 w-full last:mr-4',
            dot: `
              w-4 h-4
              rounded-full
              bg-[#1d1d1d]
              border-4 border-transparent
              data-[state=active]:bg-[#6827ff]
              data-[state=active]:border-[#1d1d1d]
              box-border`,
          }"
        >
          <NuxtLink class="relative" :to="resolveHomeLink(item.link)">
            <div
              class="p-0.25 rounded-[1.25rem] feature-gradient common-gradient"
            >
              <div class="relative h-117.5">
                <p
                  class="font-medium text-[1.375rem] tracking-wide uppercase left-6 top-6 right-10.5 absolute text-left"
                >
                  {{ item.title }}
                </p>
                <NuxtImg
                  loading="lazy"
                  format="webp"
                  class="w-full h-117.5 object-cover rounded-[1.25rem]"
                  :class="{
                    'object-[84%]': item.id === 2,
                    'object-left': item.id === 4,
                  }"
                  alt=""
                  :src="item.img"
                />
              </div>
            </div>
          </NuxtLink>
        </UCarousel>
      </ClientOnly>
    </div>

    <div class="flex flex-col gap-12.5 pt-7.5 px-2.5 lg:gap-29.5 lg:pt-35.5">
      <div
        class="font-semibold uppercase leading-[3.125rem] text-center lg:max-w-250 lg:mx-auto lg:tracking-wide"
        :class="{
          'text-2xl': isRuLocale,
          'text-[2rem] lg:text-[2.75rem]': isNotRuLocale,
        }"
        style="
          background: linear-gradient(
              107.92deg,
              #4d61fd 37.78%,
              #bb2bbf 106.06%
            )
            text;
          -webkit-text-fill-color: transparent;
        "
      >
        {{ t('home-ai-transform-heading') }}
      </div>

      <div
        class="mx-auto lg:flex lg:flex-row lg:items-start lg:gap-30 lg:relative lg:overflow-hidden"
      >
        <div class="flex gap-2.5 flex-row lg:flex-col lg:gap-15">
          <div
            v-for="item in belongTransforms.slice(0, 2)"
            :key="item.id"
            class="bg-custom-gradient h-auto text-center flex w-full max-w-50 px-2.5 min-[425px]:px-3.5 flex-col pb-1.5 lg:flex-col lg:items-start lg:text-left lg:![background:none] lg:px-0 w-full rounded-lg"
          >
            <NuxtImg
              loading="lazy"
              format="webp"
              :src="item.image"
              alt=""
              class="w-25 h-25 max-lg:mx-auto"
            />
            <div class="flex flex-col h-full lg:gap-2">
              <p
                class="uppercase font-semibold text-sm tracking-wide leading-tight break-all"
                :class="{
                  'min-[425px]:text-lg': isRuLocale,
                  'min-[425px]:text-xl': isNotRuLocale,
                }"
              >
                {{ item.title }}
              </p>
              <p
                class="text-[#969696] h-full flex text-sm items-center leading-tight tracking-wide text-xs min-[425px]:text-base break-words"
                style="word-break: break-word"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex items-center h-55.5 max-lg:relative lg:h-90.5">
          <div
            class="flex flex-row gap-17 justify-center w-full absolute lg:flex-col lg:top-15 lg:left-26 lg:gap-22.5 lg:w-auto top-0 -z-1"
          >
            <div
              v-for="(i, index) in 2"
              class="masked-element bg-[#7857c2] w-29 h-19 overflow-hidden lg:w-63.75 lg:h-21.25"
              :class="{
                '-scale-y-100 lg:scale-y-100': index === 0,
                '-scale-y-100 -scale-x-100 lg:scale-x-100': index === 1,
              }"
            >
              <div
                class="rotating-gradient-element w-58 h-58 lg:w-130 lg:h-130"
              />
            </div>
          </div>

          <NuxtImg
            loading="lazy"
            format="webp"
            src="/images/home/transforms.png"
            alt=""
            class="w-48 h-53.25 mx-auto lg:w-81.5 lg:h-90.5"
          />

          <div
            class="flex flex-row gap-17 justify-center w-full absolute max-lg:bottom-0 lg:flex-col lg:top-13 lg:right-47 lg:gap-22.5 lg:w-auto -z-1"
          >
            <div
              v-for="(i, index) in 2"
              class="masked-element bg-[#7857c2] w-29 h-19 overflow-hidden lg:w-45 lg:h-21.25"
              :class="{
                'lg:scale-y-100 lg:-scale-x-100': index === 0,
                '-scale-x-100 lg:-scale-x-100 lg:-scale-y-100': index === 1,
              }"
            >
              <div
                class="rotating-gradient-element w-58 h-58 lg:w-130 lg:h-130"
              />
            </div>
          </div>
        </div>

        <div
          class="flex gap-2.5 flex-row lg:flex-col"
          :class="{
            'lg:gap-2': isRuLocale,
            'lg:gap-15': isNotRuLocale,
          }"
        >
          <div
            v-for="item in belongTransforms.slice(2, 4)"
            :key="item.id"
            class="bg-custom-gradient h-auto text-center flex w-full max-w-50 px-2.5 min-[425px]:px-3.5 flex-col pb-1.5 lg:flex-col lg:items-start lg:text-left lg:![background:none] lg:px-0 w-full rounded-lg"
          >
            <NuxtImg
              loading="lazy"
              format="webp"
              :src="item.image"
              alt=""
              class="w-25 h-25 max-lg:mx-auto"
            />
            <div class="flex flex-col h-full lg:gap-2">
              <p
                class="uppercase font-semibold text-sm tracking-wide leading-tight break-all"
                :class="{
                  'min-[425px]:text-lg': isRuLocale,
                  'min-[425px]:text-xl': isNotRuLocale,
                }"
              >
                {{ item.title }}
              </p>
              <p
                class="text-[#969696] h-full flex text-sm items-center leading-tight tracking-wide text-xs min-[425px]:text-base break-words"
                style="word-break: break-word"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-7.5 px-5">
      <div
        class="flex flex-col gap-3 text-center lg:flex-row lg:items-end lg:justify-center lg:gap-33.5 lg:pt-22"
      >
        <div class="flex flex-col gap-16.5">
          <div class="flex flex-col lg:items-start lg:gap-3">
            <p class="font-medium text-xl tracking-wide uppercase">
              {{ t('home-ai-use-cases-label') }}
            </p>
            <p
              class="font-semibold text-[1.5rem] tracking-wide leading-6.5 lg:text-[2rem] lg:leading-normal"
              style="
                background: linear-gradient(
                    90.03deg,
                    #4d61fd -5.37%,
                    #8247df 48.86%,
                    #bb2bbf 104.19%
                  )
                  text;
                -webkit-text-fill-color: transparent;
              "
            >
              {{ t('home-ai-use-cases-title') }}
            </p>
          </div>

          <div class="flex flex-col gap-5">
            <NuxtImg
              loading="lazy"
              format="webp"
              class="max-w-97.5 w-full mx-auto lg:hidden"
              src="/images/home/use_cases_key.png"
              alt=""
            />

            <div
              class="flex flex-col gap-2 mx-auto lg:flex-row lg:flex-wrap lg:max-w-164.5"
            >
              <div
                v-for="item in useCases"
                :key="item.id"
                class="flex flex-row items-center text-start gap-6 rounded-lg max-w-97.5 pl-4 pr-10 h-16 lg:flex-col lg:justify-between lg:h-52.5 lg:w-full lg:max-w-52.5 lg:items-start lg:py-6 lg:pl-6 lg:pr-8.5"
                style="
                  background: linear-gradient(
                    218.16deg,
                    #19161c -8.71%,
                    #212121 94.29%
                  );
                "
                data-aos="fade-right"
                data-aos-easing="ease-out-cubic"
              >
                <NuxtImg
                  :src="item.image"
                  :alt="item.title"
                  class="min-w-10 max-w-10 w-10 h-10 lg:min-w-12.5 lg:w-12.5 lg:h-12.5"
                />

                <p
                  class="font-light text-base leading-5 tracking-wide lg:text-xl lg:leading-5.5"
                >
                  {{ item.title }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="relative max-[1079.9px]:mb-15">
          <NuxtImg
            loading="lazy"
            format="webp"
            class="min-w-65 w-65 max-w-65 mx-auto hidden lg:flex lg:h-130 lg:object-cover lg:mx-0"
            src="/images/home/use_cases_key_desktop.png"
            alt=""
          />

          <NuxtImg
            loading="lazy"
            format="webp"
            src="/images/home/use_cases_loading.png"
            class="w-26.5 h-26.5 absolute left-0 right-0 mx-auto bottom-43.5 rotating-element hidden object-contain lg:flex"
            alt=""
          />
        </div>
      </div>
    </div>

    <div class="relative py-7.5 px-5">
      <Icon
        name="belong:belong-product"
        class="max-w-96 w-full h-15 flex mx-auto bg-contain xl:max-w-360 xl:h-75 xl:bg-bottom xl:translate-y-10"
      />

      <div class="flex flex-row relative xl:max-w-288.5 xl:mx-auto">
        <div
          class="flex flex-col gap-3 mt-7.5 max-xl:absolute max-xl:z-1 max-xl:left-0 max-xl:right-0 max-xl:items-center"
        >
          <div
            v-for="(item, index) in product"
            :key="item.id"
            class="flex flex-row items-center max-w-94 px-3.5 gap-5 rounded-lg z-9 w-full backdrop-blur-lg"
            :style="{ zIndex: -index }"
            style="
              background: linear-gradient(
                80.34deg,
                #222222 32.04%,
                rgba(0, 0, 0, 0.1) 89.47%
              );
            "
            data-aos="fade-down"
            data-aos-easing="ease-out-cubic"
            data-aos-duration="800"
          >
            <NuxtImg
              :src="item.image"
              :alt="item.title"
              class="min-w-13 w-13 h-13"
            />

            <p
              class="text-base font-light tracking-wide leading-tight py-5 max-[375px]:text-sm"
            >
              {{ item.title }}
            </p>
          </div>
        </div>

        <NuxtImg
          loading="lazy"
          format="webp"
          class="w-65 h-130.5 -translate-y-3.3 xl:translate-y-0 mx-auto"
          src="/images/home/belong_product.png"
          alt=""
        />

        <div class="flex-col gap-3 mt-7.5 hidden xl:flex xl:static">
          <div
            v-for="(item, index) in product1"
            :key="item.id"
            class="flex flex-row items-center max-w-94 px-3.5 gap-5 rounded-lg z-9 w-full backdrop-blur-lg"
            :style="{ zIndex: -index }"
            style="
              background: linear-gradient(
                218.16deg,
                #19161c -8.71%,
                #212121 94.29%
              );
            "
            data-aos="fade-down"
            data-aos-easing="ease-out-cubic"
            data-aos-duration="800"
          >
            <NuxtImg
              :src="item.image"
              :alt="item.title"
              class="min-w-13 w-13 h-13"
            />
            <p class="text-base font-light tracking-wide leading-tight py-5">
              {{ item.title }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-268 mx-auto px-5 pt-14 lg:pt-32">
      <div class="flex flex-col gap-3 text-center mb-8 lg:mb-12">
        <p class="font-medium text-xl tracking-wide uppercase">
          {{ t('home-ai-now-label') }}
        </p>
        <p
          class="font-semibold text-[1.5rem] tracking-wide leading-7 lg:text-[2rem] lg:leading-normal"
          style="
            background: linear-gradient(
                90.03deg,
                #4d61fd -5.37%,
                #8247df 48.86%,
                #bb2bbf 104.19%
              )
              text;
            -webkit-text-fill-color: transparent;
          "
        >
          {{ t('home-ai-now-title') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div
          v-for="item in nowShipping"
          :key="item.id"
          class="rounded-xl border border-[#2B2B2B] bg-[#0D0D0D] p-5"
        >
          <Icon :name="item.icon" class="mb-5 h-9 w-9 text-[#CB99FF]" />
          <p class="font-semibold text-lg tracking-wide uppercase mb-3">
            {{ item.title }}
          </p>
          <p class="text-[#969696] text-sm leading-5 tracking-wide">
            {{ item.text }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="max-w-255.5 min-h-61.5 rounded-[0.875rem] overflow-hidden relative mx-5 lg:mx-auto"
    >
      <NuxtImg
        loading="lazy"
        format="webp"
        src="/images/home/event_ticketing.png"
        alt=""
        class="w-full h-full absolute -z-1 left-0 right-0"
      />

      <div class="flex flex-col gap-16.5 px-15 pt-10 pb-14">
        <div
          class="flex flex-row flex-wrap justify-center text-center items-center text-xl lg:text-[1.75rem] gap-3.5"
        >
          {{ t('home-ai-final-left') }}

          <Icon name="belong:arrow-ticketing" class="w-16 h-4" />

          {{ t('home-ai-final-right') }}
        </div>

        <div
          class="flex flex-wrap items-center gap-5 justify-center lg:justify-center"
        >
          <ReusableInstallButtons />

          <NuxtLink
            to="https://aideploy.co"
            target="_blank"
            rel="noopener noreferrer"
            class="p-0.25 rounded-full overflow-hidden"
            style="
              background: linear-gradient(
                85.11deg,
                #7540ee -2.65%,
                #4467ff 47.09%,
                #ff8fe9 93.65%
              );
            "
          >
            <div
              class="bg-[#000000] rounded-full flex flex-row items-center gap-2 py-2.5 px-3.5"
            >
              <Icon name="lucide:bot" class="w-5 h-5" />
              <p class="uppercase font-medium text-sm tracking-wide">
                {{ t('home-ai-hero-primary-cta') }}
              </p>
            </div>
          </NuxtLink>

          <NuxtLink
            to="/checkin"
            class="border border-[#7540EE] rounded-full flex flex-row items-center gap-2 py-2.5 px-3.5 bg-black/50"
          >
            <Icon name="lucide:scan-line" class="w-5 h-5 text-[#CB99FF]" />
            <p class="uppercase font-medium text-sm tracking-wide">
              {{ t('home-ai-hero-secondary-cta') }}
            </p>
          </NuxtLink>

          <NuxtLink
            :to="webAppLink"
            target="_blank"
            :aria-label="t('web-app')"
          >
            <div
              class="p-0.25 rounded-full overflow-hidden"
              style="
                background: linear-gradient(
                  85.11deg,
                  #7540ee -2.65%,
                  #4467ff 47.09%,
                  #ff8fe9 93.65%
                );
              "
            >
              <div
                class="bg-[#000000] rounded-full flex flex-row items-center gap-2 py-2.5 px-3.5"
              >
                <Icon name="belong:web-app" class="w-5 h-5" />
                <p class="uppercase font-medium text-sm tracking-wide">
                  {{ t('web-app') }}
                </p>
              </div>
            </div>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@reference "tailwindcss";

.masked-element {
  -webkit-mask-image: url('/images/home/mask.svg');
  mask-image: url('/images/home/mask.svg');
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position-y: bottom;
  -webkit-mask-position-x: right;
}

@media (min-width: 1023.9px) {
  .masked-element {
    -webkit-mask-image: url('/images/home/mask-desktop.svg');
    mask-image: url('/images/home/mask-desktop.svg');
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }
}

.rotating-gradient-element {
  background: linear-gradient(0deg, #230e53, #7857c2);
  animation-name: rotate-animation;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes rotate-animation {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.rotating-element {
  animation-name: rotate-animation-loop;
  animation-duration: 1.5s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes rotate-animation-loop {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.bg-custom-gradient {
  background: linear-gradient(
    148.42deg,
    rgba(13, 13, 13, 0.06) -18.27%,
    rgba(78, 78, 78, 0.2) 80.96%
  );
}

.openclaw-card {
  position: relative;
  isolation: isolate;
  min-height: 14rem;
  overflow: hidden;
  border-radius: 1.25rem;
  padding: 1.5rem;
  background:
    linear-gradient(
      148.42deg,
      rgba(13, 13, 13, 0.06) -18.27%,
      rgba(78, 78, 78, 0.2) 80.96%
    ),
    linear-gradient(218.16deg, #19161c -8.71%, #212121 94.29%);
  transition:
    border-color 180ms ease,
    transform 180ms ease,
    background 180ms ease;
}

.openclaw-card::before {
  border-radius: 1.25rem;
  background: linear-gradient(
    104.04deg,
    rgba(255, 255, 255, 0.18) 22.71%,
    rgba(255, 255, 255, 0.45) 41.98%,
    rgba(255, 255, 255, 0.16) 55.52%
  );
}

.openclaw-card:hover {
  transform: translateY(-0.25rem);
}

.openclaw-card__accent {
  position: absolute;
  inset: 0 0 auto;
  height: 0.1875rem;
  background: var(--openclaw-accent);
}

.openclaw-card__icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  margin-bottom: 1.5rem;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  background:
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.12),
      rgba(255, 255, 255, 0.03)
    ),
    var(--openclaw-accent);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.22);
}

@media (min-width: 1024px) {
  .openclaw-card {
    padding: 2rem;
  }
}

.feature-gradient::before {
  @apply rounded-[1.25rem];
  background: linear-gradient(
    104.04deg,
    rgba(255, 255, 255, 0.2) 22.71%,
    rgba(255, 255, 255, 0.5) 41.98%,
    rgba(255, 255, 255, 0.2) 55.52%
  );
}
</style>
